import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import * as actions from '@actions';
import { useRouter } from '@hooks/useRouter';
import useUser from '@vl-core/hooks/useUser';
import { useRegistrationFormInstance } from '../useSelfRegistrationFormInstance';
import { useRegistrationAlert } from './RegistrationAlertContext';

export const useProfileMutation = () => {
  const dispatch = useDispatch();
  const router = useRouter();
  const user = useUser();

  const form = useRegistrationFormInstance();

  const { setAlert } = useRegistrationAlert();

  return useMutation({
    retry: false,
    mutationFn: async (options: { complete?: boolean } | undefined) => {
      const request = form.getFieldsValue(true);

      const tel_mobile = request.tel_mobile?.replace(/^0/, '44');
      const reg_complete = options?.complete;
      const gp_excluded = user.ident_prov_code === 'NHSLOGIN' && user.auth_type === 'SSO';

      const response = await dispatch(
        actions.updateDetails({
          ...request,
          tel_mobile,
          reg_complete,
          gp_excluded
        }),
        false
      );

      if (response.error) {
        throw await response.error;
      }

      return response;
    },
    onError: (e: unknown) => {
      if (e instanceof Error) {
        setAlert({ type: 'error', message: e.message });
        return;
      }

      if (e instanceof Object && 'message' in e) {
        setAlert({ type: 'error', message: String(e.message) });
        return;
      }

      console.log('Error', e);
    },
    onSuccess: async (response, request) => {
      setAlert(undefined);

      if (request?.complete) {
        if (response?.payload.operation === 'update') {
          // refresh the user in Redux so the app reflects the newly completed profile
          await dispatch(actions.userFetch());

          // It is possible that an appointment has already been created that requires card details and that the
          // patient has only now created their account. Take them straight through to the profile page so that they
          // can register their card details.
          router.push(/*user?.needs_card_details ? '/patients/profile' : */ '/patients').then();
        } else {
          throw new Error('update failed');
        }
      }
    }
  });
};
